import React, { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ShoppingCart,
  CalendarDays,
  Package,
  Clock3,
  CircleDollarSign,
  PackageCheck,
  ChevronDown,
} from "lucide-react";

import EcommerceSalesChart from "../charts/EcommerceSalesChart";
import { useWooCommerceOrders } from "../../services/API/GetWooCommerceOrdersAPI";

/**
 * Reusable dashboard card component.
 */
const Cards = ({ title, desc, icon: Icon }) => {
  return (
    <div className="rounded-xl bg-white p-5 shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{desc}</h3>
        </div>

        {Icon && (
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-stone-100">
            <Icon className="h-6 w-6 text-[#222731]" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Returns status badge color.
 */
const getStatusBadgeClass = (status) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-700";
    case "on-hold":
      return "bg-yellow-100 text-yellow-700";
    case "cancelled":
      return "bg-red-100 text-red-700";
    case "processing":
      return "bg-blue-100 text-blue-700";
    case "pending":
      return "bg-stone-100 text-stone-700";
    default:
      return "bg-stone-100 text-stone-700";
  }
};

/**
 * Formats WooCommerce order date.
 */
const formatOrderDate = (date) => {
  if (!date) return "No date";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Formats WooCommerce order time.
 */
const formatOrderTime = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Displays the latest WooCommerce orders.
 *
 * This component allows the user to:
 * - Search orders by company, email or order number.
 * - Filter orders by status.
 * - Open the order details modal.
 */
const LatestOrdersTable = ({ orders = [], onSelectOrder }) => {
  /**
   * Stores the text entered in the search field.
   */
  const [searchTerm, setSearchTerm] = useState("");

  /**
   * Stores the currently selected order status.
   *
   * The default value "all" displays every allowed order,
   * except cancelled orders.
   */
  const [statusFilter, setStatusFilter] = useState("all");

  /**
   * Filters orders by:
   * - Order status.
   * - Billing company.
   * - Billing email.
   * - Order ID.
   *
   * Cancelled orders are hidden from the default "All Orders" view.
   * They are displayed only when the "Cancelled" filter is selected.
   */
  const filteredOrders = orders.filter((order) => {
    /**
     * Gets the current WooCommerce order status.
     */
    const orderStatus = order.status;

    /**
     * Hides cancelled orders from the default "All Orders" view.
     */
    if (statusFilter === "all" && orderStatus === "cancelled") {
      return false;
    }

    /**
     * When a specific status is selected,
     * only orders with that status are displayed.
     */
    if (statusFilter !== "all" && orderStatus !== statusFilter) {
      return false;
    }

    /**
     * Normalizes the search text.
     *
     * trim() removes spaces from the beginning and end.
     * toLowerCase() makes the search case-insensitive.
     */
    const search = searchTerm.trim().toLowerCase();

    /**
     * If the search field is empty,
     * the order passes the search filter.
     */
    if (!search) {
      return true;
    }

    /**
     * Gets the company name from the billing information.
     *
     * String() guarantees that the value can safely
     * be used with toLowerCase().
     */
    const company = String(order.billing?.company ?? "").toLowerCase();

    /**
     * Gets the customer email from the billing information.
     */
    const email = String(order.billing?.email ?? "").toLowerCase();

    /**
     * Gets the WooCommerce order ID.
     */
    const orderId = String(order.id ?? "").toLowerCase();

    /**
     * Gets all coupon codes used in the order.
     */
    const couponCodes = (order.coupon_lines ?? [])
      .map((coupon) => String(coupon.code ?? "").toLowerCase())
      .join(" ");

    /**
     * Returns true when the search text matches:
     * - Company name.
     * - Customer email.
     * - Order ID.
     * - Coupon
     */
    return (
      company.includes(search) ||
      email.includes(search) ||
      orderId.includes(search) ||
      couponCodes.includes(search)
    );
  });

  /**
   * Sorts the filtered orders from newest to oldest.
   *
   * A copied array is used to avoid modifying the original orders array.
   */
  const latestOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.date_created) - new Date(a.date_created),
  );

  return (
    <div className="h-full rounded-2xl border border-stone-100 bg-stone-50 p-4 shadow-sm">
      {/* Table title */}
      <h3 className="mb-1 text-lg font-semibold text-stone-800">
        Latest Orders
      </h3>

      {/* Table description */}
      <p className="mb-4 text-sm text-stone-500">
        Search and filter WooCommerce company orders
      </p>

      {/* Search and status filters */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row">
        {/* Company, email and order number search */}
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search by company, email, or order number..."
          className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm outline-none focus:border-stone-400"
        />

        {/* Order status filter */}
        <div className="relative w-full rounded-2xl shadow-2xs lg:w-52">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="w-full appearance-none rounded-xl border border-stone-200 bg-white px-4 py-2 pr-12 text-sm outline-none focus:border-stone-400"
          >
            <option value="all">All Orders</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Custom dropdown arrow */}
          <ChevronDown
            size={18}
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-stone-500"
          />
        </div>
      </div>

      {/* Empty state */}
      {latestOrders.length === 0 ? (
        <div className="flex h-56 items-center justify-center text-stone-400">
          No recent orders available
        </div>
      ) : (
        /* Orders table */
        <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
          {/* Orders table */}
          <table className="w-full table-fixed text-left text-sm">
            {/* Table header */}
            <thead className="sticky top-0 z-10 bg-stone-50">
              <tr className="border-b border-stone-200 text-stone-500">
                <th className="pb-3 font-medium">Order</th>

                <th className="pb-3 font-medium">Date</th>

                <th className="pb-3 font-medium">Company</th>

                <th className="pb-3 text-center font-medium">Discount</th>

                <th className="pb-3 text-center font-medium">Total</th>

                <th className="pb-3 text-center font-medium">Status</th>

                <th className="pb-3 text-center font-medium">Details</th>
              </tr>
            </thead>

            <tbody>
              {latestOrders.map((order) => {
                /**
                 * Gets the company from the WooCommerce billing information.
                 *
                 * "Unknown company" is displayed when  the company field is empty.
                 */
                const company =
                  order.billing?.company?.trim() || "Unknown company";

                /**
                 * Formats the order total using the order currency.
                 */
                const total = Number(order.total || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: order.currency || "EUR",
                });

                return (
                  <tr
                    key={order.id}
                    className="border-b border-stone-100 last:border-b-0 odd:bg-white/50"
                  >
                    {/* Order number */}
                    <td className="py-3 font-medium text-stone-700">
                      #{order.id}
                    </td>
                    {/* Order creation date and time */}
                    <td className="py-3 text-stone-500">
                      {formatOrderDate(order.date_created)}

                      <div className="text-xs text-stone-400">
                        {formatOrderTime(order.date_created)}
                      </div>
                    </td>

                    {/* Billing company */}
                    <td className="py-3 text-stone-700">{company}</td>

                    {/* Displays the discount amount and the coupon codes used */}
                    <td className="py-3 text-center">
                      {Number(order.discount_total || 0) > 0 ? (
                        <div>
                          {/* Coupon codes used in the order */}
                          <p className="mt-1 text-stone-500">
                            {order.coupon_lines?.length > 0
                              ? order.coupon_lines
                                  .map((coupon) => coupon.code)
                                  .join(", ")
                              : "No coupon"}
                          </p>
                          {/* Discount amount */}
                          <p className="font-medium text-green-700">
                            -
                            {Number(order.discount_total).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: order.currency || "EUR",
                              },
                            )}
                          </p>
                        </div>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>

                    {/* Order total */}
                    <td className="py-3 text-center font-medium text-stone-700">
                      {total}
                    </td>

                    {/* Order status */}
                    <td className="py-3 text-center">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(
                          order.status,
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>

                    {/* Opens the order details modal */}
                    <td className="py-3 text-center">
                      <button
                        type="button"
                        onClick={() => onSelectOrder(order)}
                        className="rounded-lg border border-stone-400 bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-200"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Main E-commerce dashboard page.
 */
const Ecommerce = () => {
  const { data, isPending, error } = useWooCommerceOrders();
  const [selectedOrder, setSelectedOrder] = useState(null);

  /**
   * Generates a WooCommerce-style PDF for the selected order.
   */
  const generateOrderPDF = (shouldSave = true) => {
    if (!selectedOrder) {
      return;
    }

    // Debugs the complete WooCommerce order object
    console.log("Selected WooCommerce order:", selectedOrder);

    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const currency = selectedOrder.currency || "EUR";

    /**
     * Formats monetary values for the PDF.
     */
    const formatMoney = (value) =>
      Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency,
      });

    // Creates the beige WooCommerce-style header
    doc.setFillColor(210, 183, 139);
    doc.rect(0, 0, pageWidth, 30, "F");

    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(18);

    doc.text(`Order #${selectedOrder.id}`, margin, 19);

    // Adds the introductory order information
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);

    const customerName =
      `${selectedOrder.billing?.first_name || ""} ${
        selectedOrder.billing?.last_name || ""
      }`.trim() || "Customer";

    // Adds the introductory order message
    doc.text(
      `You've received the following order from ${customerName}:`,
      margin,
      42,
    );

    // Adds the order title and date
    doc.setFont("helvetica", "bold");
    doc.setTextColor(190, 150, 90);

    doc.text(
      `Order #${selectedOrder.id} (${formatOrderDate(
        selectedOrder.date_created,
      )})`,
      margin,
      61,
    );

    // Creates the products table
    const productRows = (selectedOrder.line_items || []).map((item) => [
      item.name || "Product",
      String(item.quantity || 0),
      formatMoney(item.total),
    ]);

    autoTable(doc, {
      startY: 66,

      head: [["Product", "Quantity", "Price"]],

      body: productRows,

      theme: "grid",

      margin: {
        left: margin,
        right: margin,
      },

      tableWidth: contentWidth,

      styles: {
        font: "helvetica",
        fontSize: 8,
        textColor: [60, 60, 60],
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [50, 50, 50],
        fontStyle: "bold",
      },

      columnStyles: {
        0: {
          cellWidth: contentWidth * 0.65,
        },
        1: {
          cellWidth: contentWidth * 0.15,
          halign: "center",
        },
        2: {
          cellWidth: contentWidth * 0.2,
          halign: "right",
        },
      },
    });

    // Adds the order totals summary
    const totalsStartY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY: totalsStartY,

      body: [
        [
          "Subtotal:",
          formatMoney(
            (selectedOrder.line_items || []).reduce(
              (sum, item) => sum + Number(item.subtotal || 0),
              0,
            ),
          ),
        ],
        Number(selectedOrder.discount_total || 0) > 0
          ? ["Discount:", `-${formatMoney(selectedOrder.discount_total)}`]
          : ["Discount:", formatMoney(0)],
        ["Shipping:", "Shipping cost will be provided with order confirmation"],
        [
          "Payment method:",
          selectedOrder.payment_method_title || "Not specified",
        ],
        ["Total:", formatMoney(selectedOrder.total)],
      ],

      theme: "grid",

      margin: {
        left: margin,
        right: margin,
      },

      styles: {
        font: "helvetica",
        fontSize: 8,
        textColor: [60, 60, 60],
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
      },

      columnStyles: {
        0: {
          cellWidth: contentWidth * 0.3,
          fontStyle: "bold",
        },
        1: {
          cellWidth: contentWidth * 0.7,
        },
      },
    });

    // Gets the position below the totals table
    const addressStartY = doc.lastAutoTable.finalY + 12;

    // Adds the address section titles
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(190, 150, 90);

    doc.text("Billing address", margin, addressStartY);
    doc.text("Shipping address", pageWidth / 2 + 5, addressStartY);

    // Builds the billing address
    const billingAddress = [
      `${selectedOrder.billing?.first_name || ""} ${
        selectedOrder.billing?.last_name || ""
      }`.trim(),

      selectedOrder.billing?.company || "",

      selectedOrder.billing?.address_1 || "",
      selectedOrder.billing?.address_2 || "",

      `${selectedOrder.billing?.postcode || ""} ${
        selectedOrder.billing?.city || ""
      }`.trim(),

      selectedOrder.billing?.country || "",

      selectedOrder.billing?.phone || "",
      selectedOrder.billing?.email || "",
    ].filter(Boolean);

    // Builds the shipping address
    const shippingAddress = [
      `${selectedOrder.shipping?.first_name || ""} ${
        selectedOrder.shipping?.last_name || ""
      }`.trim(),

      selectedOrder.shipping?.company || "",

      selectedOrder.shipping?.address_1 || "",
      selectedOrder.shipping?.address_2 || "",

      `${selectedOrder.shipping?.postcode || ""} ${
        selectedOrder.shipping?.city || ""
      }`.trim(),

      selectedOrder.shipping?.country || "",
    ].filter(Boolean);

    // Adds both addresses to the PDF
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(70, 70, 70);

    // Defines the maximum width for each address column
    const addressColumnWidth = contentWidth / 2 - 8;

    // Wraps long address lines inside each column
    const billingLines = billingAddress.flatMap((line) =>
      doc.splitTextToSize(line, addressColumnWidth),
    );

    const shippingLines = shippingAddress.flatMap((line) =>
      doc.splitTextToSize(line, addressColumnWidth),
    );

    // Adds both wrapped addresses to the PDF
    doc.text(billingLines, margin, addressStartY + 7);

    doc.text(shippingLines, pageWidth / 2 + 5, addressStartY + 7);

    // Adds the PDF footer
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);

    doc.text(
      "Dashboard copy of the original order placed via memolocks.com",
      margin,
      pageHeight - 10,
    );

    doc.text(
      `Order #${selectedOrder.id}`,
      pageWidth - margin,
      pageHeight - 10,
      { align: "right" },
    );

    // Saves the generated PDF when requested
    if (shouldSave) {
      doc.save(`order-${selectedOrder.id}.pdf`);
    }

    // Returns the generated PDF document
    return doc;
  };

  /**
   * Generates and shares the selected order PDF.
   */
  const shareOrderPDF = async () => {
    if (!selectedOrder) {
      return;
    }

    // Generates the complete order PDF without downloading it
    const doc = generateOrderPDF(false);

    const pdfBlob = doc.output("blob");

    const pdfFile = new File([pdfBlob], `order-${selectedOrder.id}.pdf`, {
      type: "application/pdf",
    });

    if (
      navigator.share &&
      navigator.canShare &&
      navigator.canShare({ files: [pdfFile] })
    ) {
      try {
        await navigator.share({
          // Defines the email/share subject
          title: `Dashboard copy of order #${selectedOrder.id} from memolocks.com`,

          // Defines the email/share message
          text: `Dashboard copy of order #${selectedOrder.id} from memolocks.com`,
          files: [pdfFile],
        });
      } catch (error) {
        console.error("PDF sharing cancelled or failed:", error);
      }
    } else {
      alert("PDF sharing is not supported by this browser.");
    }
  };

  /**
   * Orders returned from the backend.
   */
  const orders = data?.data || [];

  /**
   * Orders displayed in the dashboard.
   * Includes active orders and cancelled orders for history.
   */
  const validOrders = useMemo(() => {
    return orders.filter((order) =>
      ["pending", "processing", "completed", "on-hold", "cancelled"].includes(
        order.status,
      ),
    );
  }, [orders]);

  /**
   * Orders considered successful.
   * Used for statistics and revenue calculations.
   */
  const paidOrders = validOrders.filter((order) =>
    ["completed", "on-hold"].includes(order.status),
  );

  /**
   * Total number of successful orders.
   */
  const totalOrders = paidOrders.length;

  /**
   * Total number of completed orders created during the current month.
   */
  const monthOrders = validOrders.filter((order) => {
    const orderDate = new Date(order.date_created);
    const today = new Date();

    return (
      order.status === "completed" &&
      orderDate.getMonth() === today.getMonth() &&
      orderDate.getFullYear() === today.getFullYear()
    );
  }).length;

  /**
   * Total revenue for the current year.
   * Only completed orders are included.
   */
  const yearRevenue = validOrders
    .filter((order) => {
      const orderDate = new Date(order.date_created);
      const today = new Date();

      return (
        orderDate.getFullYear() === today.getFullYear() &&
        order.status === "completed"
      );
    })
    .reduce((total, order) => total + Number(order.total || 0), 0);

  /**
   * Total number of on-hold orders.
   */
  const onHoldOrders = validOrders.filter(
    (order) => order.status === "on-hold",
  ).length;

  if (isPending) {
    return (
      <div className="flex h-80 items-center justify-center">
        <div className="rounded-xl bg-white px-6 py-4 text-stone-500 shadow">
          Loading orders...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-80 items-center justify-center">
        <div className="rounded-xl bg-white px-6 py-4 text-red-500 shadow">
          Error: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden pb-2">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Cards
          title="Total Completed Orders"
          desc={totalOrders}
          icon={PackageCheck}
        />
        <Cards title="On Hold Orders" desc={onHoldOrders} icon={Clock3} />

        <Cards title="Orders This Month" desc={monthOrders} icon={Package} />

        <Cards
          title="Revenue This Year"
          desc={yearRevenue.toLocaleString("en-US", {
            style: "currency",
            currency: "EUR",
          })}
          icon={CircleDollarSign}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="h-full">
          <LatestOrdersTable
            orders={validOrders}
            onSelectOrder={setSelectedOrder}
          />
        </section>

        <div className="h-full">
          <EcommerceSalesChart
            orders={validOrders}
            onSelectOrder={setSelectedOrder}
          />
        </div>
      </div>
      {/* Shared order details popup */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-7xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-stone-800">
                  Order #{selectedOrder.id}
                </h3>
                <p className="text-sm text-stone-500">
                  {formatOrderDate(selectedOrder.date_created)} at{" "}
                  {formatOrderTime(selectedOrder.date_created)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {/* Generates a PDF with the selected order details */}
                <button
                  type="button"
                  onClick={generateOrderPDF}
                  className="rounded-lg bg-stone-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-stone-700"
                >
                  Generate PDF
                </button>

                {/* Shares the selected order PDF */}
                <button
                  type="button"
                  onClick={shareOrderPDF}
                  className="rounded-lg bg-stone-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-stone-600"
                >
                  Send PDF
                </button>

                {/* Closes the order details */}
                <button
                  type="button"
                  onClick={() => setSelectedOrder(null)}
                  className="rounded-lg border bg-stone-100 px-3 py-1.5 text-sm font-medium text-stone-800 hover:bg-stone-200"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Customer summary */}
            <div className="mb-4 flex flex-1  gap-3 ">
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Customer</p>
                <p className="font-medium text-stone-700">
                  {`${selectedOrder.billing?.first_name || ""} ${
                    selectedOrder.billing?.last_name || ""
                  }`.trim() || "Unknown customer"}
                </p>
                <p className="text-sm text-stone-500">
                  {selectedOrder.billing?.email || "No email"}
                </p>
              </div>

              {/* Subtotal before discount */}
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Subtotal</p>

                <p className="font-medium text-stone-700">
                  {(
                    Number(selectedOrder.total || 0) +
                    Number(selectedOrder.discount_total || 0)
                  ).toLocaleString("en-US", {
                    style: "currency",
                    currency: selectedOrder.currency || "EUR",
                  })}
                </p>

                <p className="text-sm text-stone-500">Before discount</p>
              </div>

              {/* Discount summary */}
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Discount</p>

                {selectedOrder.coupon_lines?.length > 0 ? (
                  <>
                    <p className="font-medium text-green-700">
                      {selectedOrder.coupon_lines
                        .map((coupon) => coupon.code)
                        .join(", ")}
                    </p>

                    <p className="text-sm text-stone-500">
                      -
                      {Number(selectedOrder.discount_total || 0).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: selectedOrder.currency || "EUR",
                        },
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-stone-700">No Discount</p>

                    <p className="text-sm text-stone-500">
                      {Number(0).toLocaleString("en-US", {
                        style: "currency",
                        currency: selectedOrder.currency || "EUR",
                      })}
                    </p>
                  </>
                )}
              </div>

              {/* Total */}
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Total</p>
                <p className="font-medium text-stone-700">
                  {Number(selectedOrder.total || 0).toLocaleString("en-US", {
                    style: "currency",
                    currency: selectedOrder.currency || "EUR",
                  })}
                </p>
                <p className="text-sm text-stone-500">
                  Status: {selectedOrder.status}
                </p>
              </div>
            </div>

            <div className="mb-4 flex flex-1  gap-3 ">
              {/* Shipping */}
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Shipping</p>

                <div className="space-y-1 text-sm">
                  {selectedOrder.shipping?.company && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Company:
                      </span>{" "}
                      {selectedOrder.shipping.company}
                    </p>
                  )}

                  {selectedOrder.shipping?.address_1 && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Address:
                      </span>{" "}
                      {selectedOrder.shipping.address_1}
                      {","} {selectedOrder.shipping.address_2}
                    </p>
                  )}

                  {selectedOrder.shipping?.city && (
                    <p>
                      <span className="font-medium text-stone-600">City:</span>{" "}
                      {selectedOrder.shipping.city}
                      {","} {selectedOrder.shipping.state}
                    </p>
                  )}

                  {selectedOrder.shipping?.country && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Country:
                      </span>{" "}
                      {selectedOrder.shipping.country}
                      {","} {selectedOrder.shipping.postcode}
                    </p>
                  )}
                </div>
              </div>

              {/* Billing */}
              <div className="rounded-xl flex-1 bg-stone-50 p-3">
                <p className="text-xs text-stone-400">Billing</p>

                <div className="space-y-1 text-sm">
                  {(selectedOrder.billing?.first_name ||
                    selectedOrder.billing?.last_name ||
                    selectedOrder.billing?.email) && (
                    <p>
                      <span className="font-medium text-stone-600">Name:</span>{" "}
                      {selectedOrder.billing?.first_name}{" "}
                      {selectedOrder.billing?.last_name}
                      <span className="font-medium pl-2 text-stone-600">
                        -
                      </span>{" "}
                      {selectedOrder.billing?.email}
                    </p>
                  )}

                  {selectedOrder.billing?.company && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Company:
                      </span>{" "}
                      {selectedOrder.billing.company}
                    </p>
                  )}

                  {selectedOrder.billing?.address_1 && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Address:
                      </span>{" "}
                      {selectedOrder.billing.address_1}
                      {","} {selectedOrder.billing.address_2}
                    </p>
                  )}

                  {selectedOrder.billing?.city && (
                    <p>
                      <span className="font-medium text-stone-600">City:</span>{" "}
                      {selectedOrder.billing.city}
                      {","} {selectedOrder.billing.state}
                    </p>
                  )}

                  {selectedOrder.billing?.country && (
                    <p>
                      <span className="font-medium text-stone-600">
                        Country:
                      </span>{" "}
                      {selectedOrder.billing.country}
                      {","} {selectedOrder.billing.postcode}
                    </p>
                  )}

                  {(selectedOrder.billing?.phone ||
                    selectedOrder.billing?.vat_id) && (
                    <p>
                      <span className="font-medium text-stone-600">Phone:</span>{" "}
                      {selectedOrder.billing.phone}{" "}
                      <span className="font-medium pl-2 text-stone-600">
                        VAT ID:
                      </span>{" "}
                      {selectedOrder.billing?.vat_id}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <h4 className="mb-2 font-semibold text-stone-800">Items</h4>

            <div className="overflow-x-auto rounded-xl border max-h-[50vh] border-stone-100">
              <table className="w-full  min-w-[520px] text-left text-sm">
                <thead className="bg-stone-50 text-stone-500">
                  <tr>
                    <th className="p-3  font-medium">Product</th>
                    <th className="p-3 font-medium text-center">Quantity</th>
                    <th className="p-3 font-medium text-center">Total</th>
                  </tr>
                </thead>

                <tbody>
                  {(selectedOrder.line_items || []).map((item) => (
                    <tr key={item.id} className="border-t border-stone-100">
                      <td className="p-3 text-stone-700">{item.name}</td>

                      <td className="p-3 text-center text-stone-500">
                        {item.quantity}
                      </td>

                      <td className="p-3 text-center font-medium text-stone-700">
                        {Number(item.total || 0).toLocaleString("en-US", {
                          style: "currency",
                          currency: selectedOrder.currency || "EUR",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(selectedOrder.line_items || []).length === 0 && (
              <p className="mt-4 text-sm text-stone-400">
                No items found for this order.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Ecommerce;
